/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { jsonWebKeySetsUpdate } from "../funcs/jsonWebKeySetsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { JSONWebKeySet } from "../models/components/jsonwebkeyset.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateJsonWebKeySetRequest,
  UpdateJsonWebKeySetSecurity,
} from "../models/operations/updatejsonwebkeyset.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateJsonWebKeySetMutationVariables = {
  request: UpdateJsonWebKeySetRequest;
  security?: UpdateJsonWebKeySetSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateJsonWebKeySetMutationData = JSONWebKeySet;

export type UpdateJsonWebKeySetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateSet jsonWebKeySets
 *
 * @remarks
 * Update a JSON Web Key Set's name and backing external key. Requires org:admin. Both fields are replaced, not patched. Re-pointing the backing key is how rotation begins: point the set at the new external key, then publish a key from it. Already-published keys are unaffected — each keeps signing with the external key it was minted from.
 */
export function useUpdateJsonWebKeySetMutation(
  options?: MutationHookOptions<
    UpdateJsonWebKeySetMutationData,
    UpdateJsonWebKeySetMutationError,
    UpdateJsonWebKeySetMutationVariables
  >,
): UseMutationResult<
  UpdateJsonWebKeySetMutationData,
  UpdateJsonWebKeySetMutationError,
  UpdateJsonWebKeySetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateJsonWebKeySetMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateJsonWebKeySet(): MutationKey {
  return ["@gram/client", "jsonWebKeySets", "update"];
}

export function buildUpdateJsonWebKeySetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateJsonWebKeySetMutationVariables,
  ) => Promise<UpdateJsonWebKeySetMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateJsonWebKeySet(),
    mutationFn: function updateJsonWebKeySetMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateJsonWebKeySetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(jsonWebKeySetsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
